<script setup lang="ts">
import { INote } from '@/models/note'
import PopupModal from '@/components/base/PopupModal.vue'
import NoteMessage from '@/components/NoteMessage.vue'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { NotesService } from '@/services/notesService'
import { useSitesStore } from '@/stores/sites'
import { useRootStore } from '@/stores/root'
import { useUserStore } from '@/stores/user'

interface IProps {
  isOpen: boolean
  level: 'site' | 'account'
}

const props = defineProps<IProps>()
const emits = defineEmits<{
  (e: 'update:is-open', isVisible: boolean): void
}>()

const sitesStore = useSitesStore()
const rootStore = useRootStore()
const userStore = useUserStore()

const notes = ref<INote[]>([])
const totalNotesCount = ref(0)
const notesContainer = ref<HTMLElement>()
const newNote = ref('')
const newNoteError = ref('')
const isAddingNewNote = ref(false)
const isFetchingNotes = ref(false)
const scrollToBottom = ref(true)
const isPopupLoader = ref(false)
const selectedSiteId = computed(() => sitesStore.selectedSiteId)
const scrollToLastElement = async () => {
  await nextTick()
  const idx = `note-${notes.value[notes.value.length - 1]?.id}`
  const elem = document.getElementById(idx)
  elem && elem.scrollIntoView({ behavior: 'smooth' })
}
const fetchNotes = async (isLoadingMore = false) => {
  !isLoadingMore && (isFetchingNotes.value = true)
  isLoadingMore ? (scrollToBottom.value = false) : (scrollToBottom.value = true)
  !isLoadingMore && (notes.value = [])
  try {
    const data =
      props.level === 'site'
        ? await NotesService.getSiteNotes({
            siteId: sitesStore.selectedSiteId!,
            skip: notes.value?.length,
          })
        : await NotesService.getAccountNotes({
            userId: userStore.userDbId!,
            skip: notes.value?.length,
          })
    const fetchedNotes =
      props.level === 'site' ? data?.siteNotes : data?.userAccountNotes
    totalNotesCount.value = data?.total ?? 0
    notes.value.unshift(
      ...(fetchedNotes?.reverse()?.map(
        (note: any) =>
          ({
            createdBy: note.added_by,
            createdAt: note.created_at,
            updatedAt: note.updated_at,
            id: note.id,
            note: note.note,
            deletedAt: note.deletedAt,
          } as INote),
      ) ?? []),
    )
    const idx = `note-${notes.value[50]?.id}`
    const elem = document.getElementById(idx)
    elem && elem.scrollIntoView()
  } catch (e) {
    rootStore.showSnackbar('Could not fetch notes', 'error')
  } finally {
    isFetchingNotes.value = false
  }
}
const addNote = async () => {
  if (!newNote.value) {
    newNoteError.value = 'Please enter a note'
    return
  }
  isAddingNewNote.value = true
  try {
    const savedNote =
      props.level === 'site'
        ? await NotesService.createSiteNote({
            siteId: sitesStore.selectedSiteId!,
            content: newNote.value,
          })
        : await NotesService.createAccountNote({
            userId: userStore.userDbId!,
            content: newNote.value,
          })
    newNote.value = ''
    notes.value.push({
      id: savedNote.id,
      note: savedNote.note,
      createdAt: savedNote.created_at,
      updatedAt: savedNote.updated_at,
      createdBy: savedNote.added_by,
      deletedAt: savedNote.deletedAt,
    })
    await scrollToLastElement()
  } catch (e) {
    rootStore.showSnackbar('Could not add note', 'error')
  } finally {
    isAddingNewNote.value = false
  }
}

const isDeletingNote = ref(false)
const deleteNote = async (noteId: number) => {
  isDeletingNote.value = false
  isPopupLoader.value = true
  try {
    if (props.level === 'site') {
      await NotesService.deleteSiteNote({ noteId })
    } else {
      await NotesService.deleteAccountNote({
        noteId,
      })
    }
    const deletedNoteIdx = notes.value.findIndex((note) => note.id === noteId)
    notes.value[deletedNoteIdx] = {
      ...notes.value[deletedNoteIdx],
      note: 'This note has been deleted by the owner!',
      deletedAt: new Date().toISOString(),
    }
  } catch (e) {
    rootStore.showSnackbar('Could not delete note', 'error')
  } finally {
    isDeletingNote.value = false
    isPopupLoader.value = false
  }
}
const isLoadingMoreNotes = ref(false)
const loadMore = async () => {
  isLoadingMoreNotes.value = true
  try {
    await fetchNotes(true)
  } catch (e) {
    rootStore.showSnackbar('Could not load more notes', 'error')
  } finally {
    isLoadingMoreNotes.value = false
  }
}

const isRefreshingNotes = ref(false)
const refreshNotes = () => {
  isRefreshingNotes.value = true
  try {
    notes.value = []
    fetchNotes()
  } catch (e) {
    rootStore.showSnackbar('Could not refresh notes', 'error')
  } finally {
    isRefreshingNotes.value = false
  }
}

onMounted(async () => {
  userStore.isAdminUser && (await fetchNotes())
})
watch(selectedSiteId, (newValue, oldValue) => {
  if (newValue && oldValue && newValue !== oldValue && userStore.isAdminUser) {
    sitesStore.selectedSite && fetchNotes()
  }
})
watch(
  () => props.isOpen,
  async (isOpen) => {
    if (isOpen) {
      await nextTick()
      await scrollToLastElement()
    }
  },
)
</script>
<template>
  <PopupModal
    width="600"
    :title="level === 'account' ? 'Account Notes' : 'Site Notes'"
    :is-dialog-visible="isOpen"
    :show-actions="false"
    :loading="isPopupLoader"
    title-action
    @update:is-dialog-visible="emits('update:is-open', $event)"
  >
    <template #body>
      <div
        v-if="!isFetchingNotes"
        style="height: 450px"
        :style="!notes.length ? 'height: 200px !important' : ''"
      >
        <ul
          v-if="notes.length"
          id="style-3"
          ref="notesContainer"
          v-chat-scroll="{
            always: false,
            smooth: true,
            scrollonremoved: true,
            enabled: scrollToBottom,
          }"
          class="scrollbar messages"
          style="height: 90%; overflow: scroll; overflow-x: hidden"
        >
          <li class="message w-100 d-flex my-10">
            <VBtn
              v-show="
                notes.length >= 50 &&
                totalNotesCount >= 50 &&
                notes.length !== totalNotesCount
              "
              size="small"
              class="mx-auto"
              elevation="0"
              :disabled="isLoadingMoreNotes"
              :loading="isLoadingMoreNotes"
              @click.stop="loadMore"
            >
              <VIcon class="mr-2" icon="v2-icons:reload" />
              Load More
            </VBtn>
          </li>
          <li
            v-for="note in notes"
            :id="`note-${note.id}`"
            :key="note.id"
            class="force-overflow message px-2"
          >
            <NoteMessage
              :note="note"
              :deleting-note="isDeletingNote"
              @delete-note="deleteNote"
            />
          </li>
        </ul>
        <div v-if="!notes.length">
          <div id="info" class="d-flex flex-column mt-9">
            <div
              class="d-flex justify-center align-center my-3"
              style="position: relative"
            >
              <div
                class="d-flex align-center justify-center"
                style="
                  position: absolute;
                  height: 70px;
                  width: 70px;
                  border-radius: 50%;
                  background-color: rgba(var(--v-theme-grey-500), 0.2);
                "
              ></div>

              <div
                class="d-flex align-center justify-center"
                style="
                  position: absolute;
                  width: 80px;
                  height: 80px;
                  border-radius: 50%;
                  background-color: rgba(var(--v-theme-grey-500), 0.1);
                "
              ></div>
              <VIcon size="34" color="main-primary" icon="v2-icons:notepad" />
            </div>
            <div class="mt-6">No notes found</div>
          </div>
        </div>
        <div class="mt-2" style="height: 10%">
          <VTextField
            v-model="newNote"
            label="Add Note"
            :validate-on="'input'"
            :error-messages="newNoteError"
            :loading="isAddingNewNote"
            :disabled="isAddingNewNote"
            @keydown.enter.prevent="addNote"
            @update:model-value="newNoteError = ''"
          >
            <template #append>
              <VBtn icon variant="plain" elevation="0" @click="addNote">
                <VIcon
                  v-if="!isAddingNewNote"
                  class="mr-2"
                  size="34"
                  icon="tabler-send"
                />
                <VIcon v-else size="34" class="mr-2" icon="tabler-loader" />
              </VBtn>
            </template>
          </VTextField>
        </div>
      </div>
      <div v-else class="d-flex flex-column align-center justify-center">
        <VProgressCircular
          color="primary"
          class="mb-2"
          indeterminate
          size="50"
        />
        <span>Fetching Notes...</span>
      </div>
    </template>
    <template #action-with-title>
      <div class="d-flex align-center gap-2">
        <VBtn
          class="ml-2"
          color="primary"
          prepend-icon=""
          :loading="isRefreshingNotes"
          @click.stop="refreshNotes"
        >
          <v-icon size="18" color="primary">v3-icons:refresh</v-icon>
          Refresh
        </VBtn>
      </div>
    </template>
  </PopupModal>
</template>

<style scoped lang="scss">
#info {
  font-size: 18px;
  text-align: center;
  margin-bottom: 25px;
}

.scrollbar {
  overflow-y: scroll;
}

#wrapper {
  text-align: center;
  width: 500px;
  margin: auto;
}

/*
 *  STYLE 1
 */

#style-1::-webkit-scrollbar-track {
  border-radius: 10px;
  background-color: #f5f5f5;
}

#style-1::-webkit-scrollbar {
  width: 12px;
  background-color: #f5f5f5;
}

#style-1::-webkit-scrollbar-thumb {
  border-radius: 10px;
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  background-color: #555;
}

/*
 *  STYLE 2
 */

#style-2::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  background-color: #f5f5f5;
}

#style-2::-webkit-scrollbar {
  width: 12px;
  background-color: #f5f5f5;
}

#style-2::-webkit-scrollbar-thumb {
  border-radius: 10px;
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  background-color: #d62929;
}

/*
 *  STYLE 3
 */

#style-3::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  background-color: #f5f5f5;
  margin-left: 4px;
}

#style-3::-webkit-scrollbar {
  width: 5px;
  background-color: #f5f5f5;
}

#style-3::-webkit-scrollbar-thumb {
  background-color: #000000;
  margin-left: 4px;
}

/*
 *  STYLE 4
 */

#style-4::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  background-color: #f5f5f5;
}

#style-4::-webkit-scrollbar {
  width: 10px;
  background-color: #f5f5f5;
}

#style-4::-webkit-scrollbar-thumb {
  background-color: #000000;
  border: 2px solid #555555;
}

/*
 *  STYLE 5
 */

#style-5::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  background-color: #f5f5f5;
}

#style-5::-webkit-scrollbar {
  width: 10px;
  background-color: #f5f5f5;
}

#style-5::-webkit-scrollbar-thumb {
  background-color: #0ae;

  background-image: -webkit-gradient(
    linear,
    0 0,
    0 100%,
    color-stop(0.5, rgba(255, 255, 255, 0.2)),
    color-stop(0.5, transparent),
    to(transparent)
  );
}

/*
 *  STYLE 6
 */

#style-6::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  background-color: #f5f5f5;
}

#style-6::-webkit-scrollbar {
  width: 10px;
  background-color: #f5f5f5;
}

#style-6::-webkit-scrollbar-thumb {
  background-color: #f90;
  background-image: -webkit-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.2) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.2) 75%,
    transparent 75%,
    transparent
  );
}

/*
 *  STYLE 7
 */

#style-7::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  background-color: #f5f5f5;
  border-radius: 10px;
}

#style-7::-webkit-scrollbar {
  width: 10px;
  background-color: #f5f5f5;
}

#style-7::-webkit-scrollbar-thumb {
  border-radius: 10px;
  background-image: -webkit-gradient(
    linear,
    left bottom,
    left top,
    color-stop(0.44, rgb(122, 153, 217)),
    color-stop(0.72, rgb(73, 125, 189)),
    color-stop(0.86, rgb(28, 58, 148))
  );
}

/*
 *  STYLE 8
 */

#style-8::-webkit-scrollbar-track {
  border: 1px solid black;
  background-color: #f5f5f5;
}

#style-8::-webkit-scrollbar {
  width: 10px;
  background-color: #f5f5f5;
}

#style-8::-webkit-scrollbar-thumb {
  background-color: #000000;
}

/*
 *  STYLE 9
 */

#style-9::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  background-color: #f5f5f5;
}

#style-9::-webkit-scrollbar {
  width: 10px;
  background-color: #f5f5f5;
}

#style-9::-webkit-scrollbar-thumb {
  background-color: #f90;
  background-image: -webkit-linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.2) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.2) 75%,
    transparent 75%,
    transparent
  );
}

/*
 *  STYLE 10
 */

#style-10::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  background-color: #f5f5f5;
  border-radius: 10px;
}

#style-10::-webkit-scrollbar {
  width: 10px;
  background-color: #f5f5f5;
}

#style-10::-webkit-scrollbar-thumb {
  background-color: #aaa;
  border-radius: 10px;
  background-image: -webkit-linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.2) 25%,
    transparent 25%,
    transparent 50%,
    rgba(0, 0, 0, 0.2) 50%,
    rgba(0, 0, 0, 0.2) 75%,
    transparent 75%,
    transparent
  );
}

/*
 *  STYLE 11
 */

#style-11::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  background-color: #f5f5f5;
  border-radius: 10px;
}

#style-11::-webkit-scrollbar {
  width: 10px;
  background-color: #f5f5f5;
}

#style-11::-webkit-scrollbar-thumb {
  background-color: #3366ff;
  border-radius: 10px;
  background-image: -webkit-linear-gradient(
    0deg,
    rgba(255, 255, 255, 0.5) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, 0.5) 50%,
    rgba(255, 255, 255, 0.5) 75%,
    transparent 75%,
    transparent
  );
}

/*
 *  STYLE 12
 */

#style-12::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.9);
  border-radius: 10px;
  background-color: #444444;
}

#style-12::-webkit-scrollbar {
  width: 12px;
  background-color: #f5f5f5;
}

#style-12::-webkit-scrollbar-thumb {
  border-radius: 10px;
  background-color: #d62929;
  background-image: -webkit-linear-gradient(
    90deg,
    transparent,
    rgba(0, 0, 0, 0.4) 50%,
    transparent,
    transparent
  );
}

/*
 *  STYLE 13
 */

#style-13::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.9);
  border-radius: 10px;
  background-color: #cccccc;
}

#style-13::-webkit-scrollbar {
  width: 12px;
  background-color: #f5f5f5;
}

#style-13::-webkit-scrollbar-thumb {
  border-radius: 10px;
  background-color: #d62929;
  background-image: -webkit-linear-gradient(
    90deg,
    transparent,
    rgba(0, 0, 0, 0.4) 50%,
    transparent,
    transparent
  );
}

/*
 *  STYLE 14
 */

#style-14::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.6);
  background-color: #cccccc;
}

#style-14::-webkit-scrollbar {
  width: 10px;
  background-color: #f5f5f5;
}

#style-14::-webkit-scrollbar-thumb {
  background-color: #fff;
  background-image: -webkit-linear-gradient(
    90deg,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 1) 25%,
    transparent 100%,
    rgba(0, 0, 0, 1) 75%,
    transparent
  );
}

/*
 *  STYLE 15
 */

#style-15::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.1);
  background-color: #f5f5f5;
  border-radius: 10px;
}

#style-15::-webkit-scrollbar {
  width: 10px;
  background-color: #f5f5f5;
}

/*
 *  STYLE 16
 */

#style-16::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.1);
  background-color: #f5f5f5;
  border-radius: 10px;
}

#style-16::-webkit-scrollbar {
  width: 10px;
  background-color: #f5f5f5;
}

#style-16::-webkit-scrollbar-thumb {
  border-radius: 10px;
  background-color: #fff;
  background-image: -webkit-linear-gradient(
    top,
    #e4f5fc 0%,
    #bfe8f9 50%,
    #9fd8ef 51%,
    #2ab0ed 100%
  );
}
</style>
